import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, Layers, Loader2, RefreshCw, Search, Users, X, FileText } from "lucide-react";
import DepartmentSelector from "../../../../components/selectors/DepartmentSelector";
import { ApiService } from "../../../../services/api";
import type { PracticeExam, SeviyeTespitIstek, SeviyeTespitSonuc } from "../../../../types/practice-exam";

// Seviye tespiti / şube önerisi (27 Eki): şubeler + denemeler seçilir; öneri BE practice_exam/services/seviye_tespit.js'te hesaplanır.
const MAX_DENEME = 8;
const SAYI = (v: number | null | undefined, basamak = 1) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("tr-TR", { maximumFractionDigits: basamak });

export default function SeviyeTespitTab() {
  const [subeler, setSubeler] = useState<{ id: string; name: string }[]>([]);
  const [denemeler, setDenemeler] = useState<PracticeExam[]>([]);
  const [denemeYukleniyor, setDenemeYukleniyor] = useState(false);
  const [arama, setArama] = useState("");
  const [seciliDenemeler, setSeciliDenemeler] = useState<string[]>([]);
  const [alanlariAyriTut, setAlanlariAyriTut] = useState(true);
  const [kapasite, setKapasite] = useState<Record<string, string>>({});
  const [sonuc, setSonuc] = useState<SeviyeTespitSonuc | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [excelIndiriliyor, setExcelIndiriliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<"hepsi" | "degisen" | "sinirda">("hepsi");
  const [alanFiltre, setAlanFiltre] = useState<string>("hepsi");

  useEffect(() => {
    let iptal = false;
    setDenemeYukleniyor(true);
    ApiService.getPracticeExams({ limit: 200, status: "with-results" })
      .then((res: any) => {
        if (iptal) return;
        const liste: PracticeExam[] = Array.isArray(res) ? res : res?.data || res?.results || [];
        setDenemeler([...liste].sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()));
      })
      .catch(() => !iptal && setHata("Denemeler yüklenemedi"))
      .finally(() => !iptal && setDenemeYukleniyor(false));
    return () => { iptal = true; };
  }, []);

  // Seçim değişince eski sonuç ve kapasiteler geçersiz
  useEffect(() => { setSonuc(null); setKapasite({}); }, [subeler, seciliDenemeler, alanlariAyriTut]);

  const gorunenDenemeler = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr-TR");
    return q ? denemeler.filter((d) => d.name.toLocaleLowerCase("tr-TR").includes(q)) : denemeler;
  }, [denemeler, arama]);

  const istek = (kapasiteyle: boolean): SeviyeTespitIstek => {
    const kap: Record<string, number> = {};
    if (kapasiteyle) Object.entries(kapasite).forEach(([id, v]) => { if (v !== "" && Number.isFinite(Number(v))) kap[id] = Math.max(0, Math.round(Number(v))); });
    return { departmentIds: subeler.map((s) => s.id), examIds: seciliDenemeler, alanlariAyriTut, ...(Object.keys(kap).length ? { kapasite: kap } : {}) };
  };

  const calistir = async (kapasiteyle = false) => {
    setYukleniyor(true);
    setHata(null);
    try {
      const out = await ApiService.runSeviyeTespit(istek(kapasiteyle));
      setSonuc(out);
      if (!kapasiteyle) setKapasite(Object.fromEntries(out.subeler.map((s) => [s.id, String(s.kapasite)])));
    } catch (e: any) {
      setHata(e?.message || "Rapor çalıştırılamadı");
    } finally {
      setYukleniyor(false);
    }
  };

  const excelIndir = async () => {
    setExcelIndiriliyor(true);
    try {
      const { blob, fileName } = await ApiService.downloadSeviyeTespitExcel(istek(true));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `seviye-tespiti-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setHata(e?.message || "Excel indirilemedi");
    } finally {
      setExcelIndiriliyor(false);
    }
  };

  const hazir = subeler.length > 0 && seciliDenemeler.length > 0;
  const alanSecenekleri = useMemo(() => [...new Set((sonuc?.ogrenciler || []).map((o) => o.alanEtiket))], [sonuc]);
  const ogrenciler = useMemo(() => (sonuc?.ogrenciler || []).filter((o) =>
    (filtre === "hepsi" || (filtre === "degisen" ? o.degisiyor : o.sinirda)) && (alanFiltre === "hepsi" || o.alanEtiket === alanFiltre)), [sonuc, filtre, alanFiltre]);
  const kapasiteDegisti = sonuc ? sonuc.subeler.some((s) => String(s.kapasite) !== (kapasite[s.id] ?? String(s.kapasite))) : false;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Seçim */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Seviye Tespiti ve Şube Önerisi</h2>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
          Seçtiğin şubelerin öğrencileri, seçtiğin denemelerdeki yalnız alan testlerinin netine göre sıralanır (SAY: Matematik + Fen, EA: Edebiyat + Matematik, SÖZ: Edebiyat + Sosyal; TYT testleri herkese ortak, LGS'de MEB ağırlıkları).
          Şube mevcutları korunarak, en az yer değiştirmeyle seviye şubeleri önerilir. Hiçbir şey kaydedilmez.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Şubeler (sınıf seviyesi seçilirse şubelerine açılır)
            </label>
            <DepartmentSelector
              value={null}
              onChange={(id, dep) => {
                if (!id || subeler.some((s) => s.id === id)) return;
                setSubeler((prev) => [...prev, { id, name: dep?.name || id }]);
              }}
              placeholder="Şube ekle"
              allowClear={false}
              showColors={true}
              className="text-sm"
            />
            {subeler.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {subeler.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-900 text-xs sm:text-sm">
                    {s.name}
                    <button type="button" onClick={() => setSubeler((prev) => prev.filter((x) => x.id !== s.id))} className="text-blue-700 hover:text-blue-900" aria-label={`${s.name} kaldır`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <label className="flex items-start gap-2 mt-3 text-xs sm:text-sm text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={alanlariAyriTut} onChange={(e) => setAlanlariAyriTut(e.target.checked)} />
              <span>Azınlıktaki alan (ör. EA) birlikte kalsın; çoğunluk alanı seviyeye göre dağıtılsın</span>
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Denemeler ({seciliDenemeler.length}/{MAX_DENEME}) — birden çok deneme daha güvenilir
            </label>
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={arama}
                onChange={(e) => setArama(e.target.value)}
                placeholder="Deneme ara"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="border border-gray-300 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-200">
              {denemeYukleniyor ? (
                <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin text-blue-600" /> Yükleniyor</div>
              ) : gorunenDenemeler.length === 0 ? (
                <div className="px-3 py-3 text-sm text-gray-500">Sonucu olan deneme yok</div>
              ) : gorunenDenemeler.map((d) => {
                const secili = seciliDenemeler.includes(d.id);
                const dolu = !secili && seciliDenemeler.length >= MAX_DENEME;
                return (
                  <label key={d.id} className={`flex items-center gap-2 px-3 py-2 text-xs sm:text-sm ${secili ? "bg-blue-50 text-blue-900 font-medium" : dolu ? "text-gray-400" : "hover:bg-gray-50 text-gray-800"}`}>
                    <input
                      type="checkbox"
                      checked={secili}
                      disabled={dolu}
                      onChange={() => setSeciliDenemeler((prev) => (secili ? prev.filter((x) => x !== d.id) : [...prev, d.id]))}
                    />
                    <span className="flex-1 min-w-0 truncate">{d.name}</span>
                    {d.date && <span className="text-gray-500 flex-shrink-0">{new Date(d.date).toLocaleDateString("tr-TR")}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mt-3 sm:mt-4">
          <button
            onClick={() => calistir(false)}
            disabled={!hazir || yukleniyor}
            className={`px-4 sm:px-6 py-2 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-2 ${hazir && !yukleniyor ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
          >
            {yukleniyor && <Loader2 className="w-4 h-4 animate-spin" />}
            Raporu Çalıştır
          </button>
          {sonuc && (
            <button
              onClick={excelIndir}
              disabled={excelIndiriliyor}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              {excelIndiriliyor ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <Download className="w-4 h-4" />}
              Excel indir
            </button>
          )}
        </div>
        {hata && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{hata}</div>}
      </div>

      {sonuc && (
        <>
          {/* Özet */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {[
              ["Öğrenci", sonuc.ozet.ogrenci],
              ["Şube değiştiren", sonuc.ozet.degisen],
              ["Sınırda", sonuc.ozet.sinirda],
              ["Denemeye girmeyen", sonuc.ozet.girmeyen.length],
            ].map(([ad, deger]) => (
              <div key={String(ad)} className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
                <div className="text-xs sm:text-sm text-gray-600">{ad}</div>
                <div className="text-xl sm:text-2xl font-semibold text-gray-900">{deger}</div>
              </div>
            ))}
          </div>

          {sonuc.uyarilar.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-900 mb-1"><AlertTriangle className="w-4 h-4" /> Dikkat</div>
              <ul className="list-disc pl-5 text-xs sm:text-sm text-amber-900 space-y-1">
                {sonuc.uyarilar.map((u) => <li key={u}>{u}</li>)}
              </ul>
            </div>
          )}

          {/* Şubeler */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Şubeler: mevcut ve önerilen</h3>
              <button
                onClick={() => calistir(true)}
                disabled={!kapasiteDegisti || yukleniyor}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${kapasiteDegisti && !yukleniyor ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
              >
                <RefreshCw className="w-4 h-4" /> Kapasiteyle yeniden hesapla
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs sm:text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Şube</th>
                    <th className="py-2 pr-3 font-medium">Kapasite</th>
                    <th className="py-2 pr-3 font-medium">Alan</th>
                    <th className="py-2 pr-3 font-medium">Mevcut (kişi · ort. net)</th>
                    <th className="py-2 pr-3 font-medium">Önerilen (kişi · ort. net · aralık)</th>
                    <th className="py-2 pr-3 font-medium">Seviye</th>
                  </tr>
                </thead>
                <tbody>
                  {sonuc.subeler.map((s) => s.alanlar.map((a, i) => (
                    <tr key={`${s.id}-${a.alan}`} className="border-b border-gray-100">
                      <td className="py-2 pr-3 font-medium text-gray-900">{i === 0 ? s.ad : ""}</td>
                      <td className="py-2 pr-3">
                        {i === 0 && (
                          <input
                            type="number"
                            min={0}
                            value={kapasite[s.id] ?? String(s.kapasite)}
                            onChange={(e) => setKapasite((prev) => ({ ...prev, [s.id]: e.target.value }))}
                            className="w-16 px-2 py-1 border border-gray-300 rounded-md text-sm"
                          />
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-700">{a.etiket}</td>
                      <td className="py-2 pr-3 text-gray-700">{a.mevcut.n} · {SAYI(a.mevcut.ort)}</td>
                      <td className="py-2 pr-3 text-gray-900">{a.oneri.n} · {SAYI(a.oneri.ort)}{a.oneri.n ? ` · ${SAYI(a.oneri.min)}–${SAYI(a.oneri.max)}` : ""}</td>
                      <td className="py-2 pr-3 text-blue-700 font-medium">{a.seviye || ""}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            {sonuc.kesmeler.length > 0 && (
              <p className="text-xs sm:text-sm text-gray-600 mt-3">
                Kesme puanları: {sonuc.kesmeler.map((k) => `${k.ust} / ${k.alt}: ${SAYI(k.kesme, k.kesme < 1.5 ? 3 : 1)} (sınırda ${k.sinirda})`).join(" · ")}.
                Ölçme hatası: {sonuc.alanlar.map((a) => `${a.etiket} ±${SAYI(a.olcmeHatasi)} net`).join(", ")}.
              </p>
            )}
          </div>

          {/* Öğrenciler */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Öğrenciler</h3>
              <div className="flex flex-wrap gap-4 text-xs sm:text-sm text-gray-600">
                {([["hepsi", "Hepsi"], ["degisen", "Şube değiştirenler"], ["sinirda", "Sınırdakiler"]] as const).map(([k, ad]) => (
                  <button key={k} onClick={() => setFiltre(k)} className={`pb-1 ${filtre === k ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"}`}>{ad}</button>
                ))}
                {alanSecenekleri.length > 1 && (
                  <select value={alanFiltre} onChange={(e) => setAlanFiltre(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1 text-xs sm:text-sm">
                    <option value="hepsi">Bütün alanlar</option>
                    {alanSecenekleri.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs sm:text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Sıra</th>
                    <th className="py-2 pr-3 font-medium">Öğrenci</th>
                    <th className="py-2 pr-3 font-medium">Alan</th>
                    <th className="py-2 pr-3 font-medium">Mevcut</th>
                    <th className="py-2 pr-3 font-medium">Önerilen</th>
                    <th className="py-2 pr-3 font-medium">Alan neti</th>
                    <th className="py-2 pr-3 font-medium">Başarı</th>
                    {sonuc.ozet.denemeler.map((d) => <th key={d.id} className="py-2 pr-3 font-medium whitespace-nowrap">{d.ad}</th>)}
                    <th className="py-2 pr-3 font-medium">Not</th>
                  </tr>
                </thead>
                <tbody>
                  {ogrenciler.map((o) => (
                    <tr key={o.id} className={`border-b border-gray-100 ${o.sinirda ? "bg-amber-50" : ""}`}>
                      <td className="py-2 pr-3 text-gray-700">{o.sira ?? "—"}</td>
                      <td className="py-2 pr-3 text-gray-900 whitespace-nowrap">{o.ad}</td>
                      <td className="py-2 pr-3 text-gray-700" title={o.alanKaynak === "cikarim" ? "Hesapta alan kayıtlı değil; işaretlenen testlerden çıkarıldı" : undefined}>
                        {o.alanEtiket}{o.alanKaynak === "cikarim" ? "*" : ""}
                      </td>
                      <td className="py-2 pr-3 text-gray-700">{o.mevcutSube}</td>
                      <td className={`py-2 pr-3 ${o.degisiyor ? "text-blue-700 font-semibold" : "text-gray-700"}`}>
                        {o.oneriSube}{o.seviye ? <span className="text-gray-500 font-normal"> · {o.seviye}</span> : null}
                      </td>
                      <td className="py-2 pr-3 text-gray-900 whitespace-nowrap">{SAYI(o.toplam, 2)} / {SAYI(o.maxNet, 0)}</td>
                      <td className="py-2 pr-3 text-gray-700">{o.basari === null ? "—" : `%${o.basari}`}</td>
                      {o.denemeler.map((d) => (
                        <td key={d.denemeId} className={`py-2 pr-3 whitespace-nowrap ${d.tahmin ? "text-gray-400 italic" : "text-gray-700"}`} title={d.tahmin ? "Bu denemeye girmedi; alan içi konumundan tahmin" : undefined}>
                          {d.net === null ? "—" : `${SAYI(d.net, 2)} / ${SAYI(d.max, 0)}`}
                        </td>
                      ))}
                      <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">{[o.sinirda ? "Sınırda" : "", o.eksik.length ? "Tahminli" : ""].filter(Boolean).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ogrenciler.length === 0 && <div className="text-sm text-gray-500 py-4 text-center">Bu süzgeçte öğrenci yok</div>}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Sarı satır: kesme puanına ölçme hatası kadar yakın; öğretmen görüşüyle kesinleştirin. Gri italik: o denemeye girmedi, alan içi konumundan tahmin edildi.
              Alanın yanındaki *: hesapta alan kayıtlı değil, işaretlenen testlerden çıkarıldı.
            </p>
          </div>

          {/* Yer değiştirme + kazanım + ölçüm */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Yer değiştirme</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b border-gray-200">
                      <th className="py-2 pr-3 font-medium">Mevcut \ Önerilen</th>
                      {sonuc.subeler.map((s) => <th key={s.id} className="py-2 pr-3 font-medium">{s.ad}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {sonuc.hareket.map((h) => (
                      <tr key={h.from} className="border-b border-gray-100">
                        <td className="py-2 pr-3 font-medium text-gray-900">{h.from}</td>
                        {sonuc.subeler.map((s) => (
                          <td key={s.id} className={`py-2 pr-3 ${h.from === s.ad ? "text-gray-900 font-medium" : (h.to[s.ad] || 0) > 0 ? "text-blue-700" : "text-gray-400"}`}>{h.to[s.ad] || 0}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sonuc.ozet.girmeyen.length > 0 && (
                <p className="text-xs sm:text-sm text-gray-600 mt-3">Denemeye girmeyen (şubesinde bırakıldı): {sonuc.ozet.girmeyen.map((g) => `${g.ad} (${g.sube})`).join(", ")}</p>
              )}
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Ölçüm</h3>
              <div className="space-y-2 text-xs sm:text-sm text-gray-700">
                {sonuc.alanlar.map((a) => (
                  <div key={a.alan}>
                    <div className="font-medium text-gray-900">{a.etiket}: {a.ogrenci} öğrenci, ölçme hatası ±{SAYI(a.olcmeHatasi)} net</div>
                    <ul className="list-disc pl-5">
                      {a.guvenirlik.map((g) => (
                        <li key={`${g.deneme}-${g.test}`}>{g.deneme} · {g.test}: güvenirlik {g.kr20 ?? "—"}, ort. {SAYI(g.ortalamaDogru)}/{g.soru} doğru{g.tavanOrani >= 40 ? `, tavana yakın %${g.tavanOrani}` : ""}</li>
                      ))}
                      {a.tutarlilik.map((t) => <li key={t.denemeler.join("|")}>İki deneme sıra uyumu {t.siraUyumu ?? "—"} ({t.ogrenci} öğrenci)</li>)}
                    </ul>
                  </div>
                ))}
                <p className="text-gray-500">Kazanım düzeyi öğrenci yerleştirmesinde kullanılmaz: kazanım başına birkaç soru düşer, tek öğrenci için tutarsızdır. Aşağıdaki liste şube düzeyinde öğretmen planı içindir.</p>
              </div>
            </div>
          </div>

          {sonuc.kazanimEksikleri.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Önerilen şubelerin en çok kaçırdığı konular</h3>
              <div className="space-y-2">
                {sonuc.subeler.map((s) => {
                  const gruplar = sonuc.kazanimEksikleri.filter((g) => g.subeId === s.id);
                  if (!gruplar.length) return null;
                  return (
                    <details key={s.id} className="border border-gray-200 rounded-lg">
                      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-900">{s.ad}</summary>
                      <div className="px-3 pb-3 space-y-3">
                        {gruplar.map((g) => (
                          <div key={`${g.alan}-${g.denemeId}`}>
                            <div className="text-xs sm:text-sm text-gray-600 mb-1">{g.alanEtiket} · {g.deneme}</div>
                            <ul className="text-xs sm:text-sm text-gray-800 space-y-0.5">
                              {g.kazanimlar.map((k) => (
                                <li key={`${k.ders}-${k.kazanim}`} className="flex gap-2">
                                  <span className="w-12 flex-shrink-0 text-right font-medium text-gray-900">%{k.oran}</span>
                                  <span className="text-gray-500 flex-shrink-0">{k.ders}</span>
                                  <span className="min-w-0">{k.kazanim}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
